import { StateCreator } from 'zustand';
import { CedarStore } from './types';
import { getSupabaseClient, SupabaseConfig } from '@/utils/supabase';

// Define the config slice
export interface ConfigSlice {
	productId: string | null;
	supabaseConfig: SupabaseConfig | null;
	setProductId: (productId: string) => void;
	setSupabaseConfig: (config: SupabaseConfig) => void;
	validateOrCreateProductUser: () => Promise<{
		success: boolean;
		error: Error | null;
	}>;
	updateProductUserId: (
		oldId: string,
		newId: string
	) => Promise<{ success: boolean; error: Error | null }>;
	addEmailToProductUser: (
		email: string,
		id?: string
	) => Promise<{ success: boolean; error: Error | null }>;
}

// Create the config slice implementation
export const createConfigSlice: StateCreator<
	CedarStore,
	[],
	[],
	ConfigSlice
> = (set, get) => ({
	// Default state
	productId: null,
	supabaseConfig: null,

	// Actions
	setProductId: (productId: string) =>
		set(() => ({
			productId,
		})),

	setSupabaseConfig: (config: SupabaseConfig) =>
		set(() => ({
			supabaseConfig: config,
		})),

	validateOrCreateProductUser: async () => {
		const userId = get().userId;
		const productId = get().productId;
		const config = get().supabaseConfig;

		if (!userId) {
			return {
				success: false,
				error: new Error('User ID is required but not set in store'),
			};
		}

		if (!productId) {
			return {
				success: false,
				error: new Error('Product ID is required but not set in store'),
			};
		}

		return validateOrCreateProductUser(userId, productId, config ?? undefined);
	},

	updateProductUserId: async (oldId: string, newId: string) => {
		const productId = get().productId;
		const config = get().supabaseConfig;

		if (!productId) {
			return {
				success: false,
				error: new Error('Product ID is required but not set in store'),
			};
		}

		if (!oldId || !newId) {
			return {
				success: false,
				error: new Error('Both oldId and newId are required'),
			};
		}

		return supabaseUpdateProductUserId(
			oldId,
			newId,
			productId,
			config ?? undefined
		);
	},

	addEmailToProductUser: async (email: string, id?: string) => {
		const productUserId = id ?? get().userId;
		const productId = get().productId;
		const config = get().supabaseConfig;

		if (!productUserId) {
			return {
				success: false,
				error: new Error('User ID is required but not set in store'),
			};
		}

		if (!productId) {
			return {
				success: false,
				error: new Error('Product ID is required but not set in store'),
			};
		}

		return supabaseAddEmailToProductUser(
			productUserId,
			email,
			config ?? undefined
		);
	},
});

/**
 * Validates if a product user exists, and creates one if not
 * @param userId User ID to check
 * @param productId Product ID to check
 * @param config Optional Supabase configuration
 * @returns Object with success status and error if any
 */
export const validateOrCreateProductUser = async (
	userId: string,
	productId: string,
	config?: SupabaseConfig
): Promise<{ success: boolean; error: Error | null }> => {
	const client = getSupabaseClient(config);

	try {
		// Check if product_user exists with this userId and productId
		const { data: productUserData, error: productUserError } = await client
			.from('product_users')
			.select('id')
			.eq('id', userId)
			.eq('product_id', productId)
			.maybeSingle();

		if (productUserError) {
			console.error('Error checking product_user:', productUserError);
			return { success: false, error: productUserError };
		}

		// If product_user doesn't exist, create it
		if (!productUserData) {
			const { error: createError } = await client.from('product_users').insert({
				id: userId,
				product_id: productId,
				metadata: null,
			});

			if (createError) {
				console.error('Error creating product_user:', createError);
				return { success: false, error: createError };
			}
		}

		return { success: true, error: null };
	} catch (err) {
		console.error('Error validating or creating product user:', err);
		return { success: false, error: err as Error };
	}
};

/**
 * Updates the primary key (id) of an existing product_users row.
 */
export const supabaseUpdateProductUserId = async (
	oldId: string,
	newId: string,
	productId: string,
	config?: SupabaseConfig
): Promise<{ success: boolean; error: Error | null }> => {
	const client = getSupabaseClient(config);
	try {
		const { error } = await client
			.from('product_users')
			.update({ id: newId })
			.eq('id', oldId)
			.eq('product_id', productId);

		if (error) {
			console.error('Error updating product_user id:', error);
			return { success: false, error };
		}

		return { success: true, error: null };
	} catch (err) {
		console.error('Error updating product user id:', err);
		return { success: false, error: err as Error };
	}
};

/**
 * Adds or updates the e-mail address for the specified product_user row.
 */
export const supabaseAddEmailToProductUser = async (
	productUserId: string,
	email: string,
	config?: SupabaseConfig
): Promise<{ success: boolean; error: Error | null }> => {
	const client = getSupabaseClient(config);
	try {
		const { error } = await client
			.from('product_users')
			.update({ email } as Record<string, unknown>)
			.eq('id', productUserId);

		if (error) {
			console.error('Error adding email to product_user:', error);
			return { success: false, error };
		}

		return { success: true, error: null };
	} catch (err) {
		console.error('Error adding email to product user:', err);
		return { success: false, error: err as Error };
	}
};
